import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import CarCard from "../components/CarCard";
import { MaskLine } from "../components/Reveal";
import { useLang } from "../i18n/LanguageContext";
import { api } from "../lib/api";

export default function Inventory() {
  const { t, fmtNum } = useLang();
  const [cars, setCars] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [brand, setBrand] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/cars").then((r) => setCars(r.data)).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const brands = useMemo(() => [...new Set(cars.map((c) => c.brand).filter(Boolean))].sort(), [cars]);

  const filtered = useMemo(
    () =>
      cars.filter((c) => {
        if (brand !== "all" && c.brand !== brand) return false;
        if (status !== "all" && c.status !== status) return false;
        if (q && !`${c.brand} ${c.model}`.toLowerCase().includes(q.toLowerCase())) return false;
        return true;
      }),
    [cars, brand, status, q]
  );

  return (
    <main className="pt-32 md:pt-40 pb-24 min-h-screen" data-testid="inventory-page">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <p className="eyebrow mb-5">LATUAUTONLINE</p>
        <h1 className="display-title text-6xl md:text-8xl mb-4">
          <MaskLine>{t("inventory.title")}</MaskLine>
        </h1>
        <p className="text-smoke max-w-md mb-12">{t("inventory.sub")}</p>

        <div className="flex flex-col md:flex-row md:items-end gap-4 md:gap-6 mb-10 border-b border-ink/10 pb-8" data-testid="inventory-filters">
          <div className="flex-1">
            <label className="field-label" htmlFor="inv-search">{t("filter.search")}</label>
            <input
              id="inv-search"
              data-testid="inventory-search"
              className="field md:max-w-xs"
              placeholder={t("filter.search")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div>
            <label className="field-label" htmlFor="inv-brand">{t("f.brand")}</label>
            <select id="inv-brand" data-testid="inventory-filter-brand" className="field md:w-48" value={brand} onChange={(e) => setBrand(e.target.value)}>
              <option value="all">{t("filter.allBrands")}</option>
              {brands.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="field-label" htmlFor="inv-status">{t("f.status")}</label>
            <select id="inv-status" data-testid="inventory-filter-status" className="field md:w-40" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="all">{t("filter.all")}</option>
              <option value="new">{t("status.new")}</option>
              <option value="used">{t("status.used")}</option>
            </select>
          </div>
        </div>

        <p className="font-mono text-xs text-fog tracking-[0.2em] uppercase mb-8" data-testid="inventory-count">
          {fmtNum(filtered.length)} {t("inventory.results")}
        </p>

        {loaded && filtered.length === 0 ? (
          <p className="text-fog py-20 text-center font-display text-2xl italic">{t("inventory.empty")}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8">
            {filtered.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: Math.min(i * 0.06, 0.4), ease: [0.16, 1, 0.3, 1] }}
              >
                <CarCard car={c} />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
